import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { mockProjects as mockProjectsEn } from "@/mocks/projects";
import { mockProjects as mockProjectsPs } from "@/mocks/ps/projects";
import { mockProjects as mockProjectsDa } from "@/mocks/da/projects";

const localizedProjects: Record<string, typeof mockProjectsEn> = {
  en: mockProjectsEn,
  fr: mockProjectsEn,
  de: mockProjectsEn,
  ps: mockProjectsPs,
  da: mockProjectsDa,
};

const categoryColors: Record<string, string> = {
  Education: "bg-amber-100 text-amber-700",
  Infrastructure: "bg-sky-100 text-sky-700",
  Healthcare: "bg-rose-100 text-rose-700",
  Community: "bg-violet-100 text-violet-700",
  Humanitarian: "bg-orange-100 text-orange-700",
};

export default function ProjectsPreview() {
  const { t, i18n } = useTranslation();
  const headerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const mockProjects = localizedProjects[i18n.language] || mockProjectsEn;
  const featured = mockProjects.slice(0, 3);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("reveal-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );

    if (headerRef.current) {
      headerRef.current.classList.add("reveal");
      observer.observe(headerRef.current);
    }

    if (gridRef.current) {
      const children = Array.from(gridRef.current.children) as HTMLElement[];
      children.forEach((child, i) => {
        child.classList.add("reveal-child");
        child.style.transitionDelay = `${i * 150}ms`;
      });
      observer.observe(gridRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12" ref={headerRef}>
          <span className="text-xs font-semibold uppercase tracking-widest text-[#4ECDC4] mb-3 block">
            {t("projects.tag")}
          </span>
          <h2
            className="text-3xl md:text-4xl font-bold text-[#1B2838] mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("projects.title")}
          </h2>
          <p className="text-gray-500 text-base max-w-xl mx-auto">{t("projects.subtitle")}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mb-10" ref={gridRef}>
          {featured.map((project) => {
            const pct = Math.round((project.raised / project.goal) * 100);
            return (
              <div
                key={project.id}
                className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-[#4ECDC4]/40 transition-all duration-300 group"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3">
                    <span
                      className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                        categoryColors[project.category] || "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {project.category}
                    </span>
                  </div>
                  {project.status === "completed" && (
                    <div className="absolute top-3 right-3 bg-green-500 text-white text-xs font-semibold px-2.5 py-1 rounded-full">
                      {t("projects.completed")}
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="font-bold text-[#1B2838] text-base mb-2 leading-tight">
                    {project.title}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4 line-clamp-2">
                    {project.description}
                  </p>

                  <div className="mb-3">
                    <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                      <span>
                        {t("projects.raised")}:{" "}
                        <strong className="text-[#1B2838]">
                          ${project.raised.toLocaleString()}
                        </strong>
                      </span>
                      <span className="font-semibold text-[#4ECDC4]">{pct}%</span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#4ECDC4] rounded-full transition-all duration-700"
                        style={{ width: `${Math.min(pct, 100)}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 text-gray-400 text-xs">
                      <i className="ri-map-pin-line"></i>
                      <span>{project.location}</span>
                    </div>
                    <Link
                      to={`/projects/${project.id}`}
                      className="text-[#4ECDC4] text-sm font-semibold hover:text-[#3dbdb5] cursor-pointer flex items-center gap-1"
                    >
                      {t("projects.learnMore")} <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-center">
          <Link
            to="/projects"
            className="inline-flex items-center gap-2 px-8 py-3 border-2 border-[#1B2838] text-[#1B2838] rounded-full font-semibold text-sm whitespace-nowrap cursor-pointer hover:bg-[#1B2838] hover:text-white transition-all"
          >
            {t("projects.viewAll")}
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}